import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay, map } from 'rxjs/operators';
import { doc, updateDoc, serverTimestamp, getDoc, onSnapshot, setDoc, collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { environment } from 'env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

export interface InviteEligibilityResult {
  eligible: boolean;
  hasVerifiedMembership: boolean;
  isT1Elite: boolean;
  meetsCommunityCriteria: boolean;
  connectionsCount: number;
  reviewsCount: number;
  accountAgeMonths: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userProfileSubject = new BehaviorSubject<any | null>(null);
  public readonly userProfile$: Observable<any | null> = this.userProfileSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly idv$: Observable<any> = this.userProfile$.pipe(
    map(profile => profile?.idv ?? null),
    distinctUntilChanged(),
    shareReplay(1)
  );

  private profileUnsubscribe?: () => void;

  private storage = getStorage(this.firebaseService.app, environment.imgBucket);

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private http: HttpClient
  ) {
    this.authService.user$.subscribe(user => {
      if (user) {
        this.listenToUserProfile(user.uid);
      } else {
        this.userProfileSubject.next(null);
        this.stopListeningToProfile();
      }
    });
  }

  // General method to update user profile fields
  async updateUserProfile(updates: Partial<any>): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found. Please sign in first.');
    }

    try {
      const userDocRef = doc(this.firebaseService.firestore, 'users', user.uid);
      const updatedData = {
        ...updates,
        lastUpdated: serverTimestamp()
      };

      await updateDoc(userDocRef, updatedData);

    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Upload profile photo to GCS with userId naming
  async uploadProfilePhoto(file: File): Promise<string> {
    const user = this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user found.');
    try {
      const fileExtension = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const filePath = `images/${user.uid}_${Date.now()}.${fileExtension}`;
      const imgIxPath = `${user.uid}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(this.storage, filePath);
      await uploadBytes(storageRef, file);
      const imgIXUrl = `https://${environment.imgIxDomain}.imgix.net/${imgIxPath}`;
      await this.updateUserProfile({ profileImgURL: imgIXUrl });
      return imgIXUrl;
    } catch (error) {
      console.error('Upload error details:', {
        code: error.code,
        message: error.message,
        serverResponse: error.serverResponse,
        name: error.name,
        stack: error.stack
      });
      throw error;
    }
  }

  // Fetch user profile once (non-reactive)
  async getUserProfile(): Promise<any> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    }

    try {
      const userDocRef = doc(this.firebaseService.firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const profile = userDocSnap.data();
        return {
          id: user.uid,
          ...profile
        };
      } else {
        throw new Error('User profile not found.');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async getProfileById(id): Promise<any> {
    try {
      const userDocRef = doc(this.firebaseService.firestore, 'users', id);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const profile = userDocSnap.data();
        return {
          id: id,
          ...profile
        };
      } else {
        throw new Error('User profile not found.');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async getRatingStats(id: string): Promise<{ averageRating: number; totalReviews: number }> {
    try {
      const reviewsRef = collection(this.firebaseService.firestore, `users/${id}/reviews`);
      const snapshot = await getDocs(reviewsRef);

      let total = 0;
      snapshot.forEach(docSnap => {
        const data = docSnap.data() as any;
        if (typeof data.rating === 'number') {
          total += data.rating;
        }
      });

      const totalReviews = snapshot.size;
      const averageRating = totalReviews > 0 ? total / totalReviews : 0;

      return { averageRating, totalReviews };
    } catch (error) {
      console.error('Error fetching user rating:', error);
      return { averageRating: 0, totalReviews: 0 };
    }
  }

  async getInviteEligibility(): Promise<InviteEligibilityResult> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found. Please sign in first.');
    }

    let profile = this.userProfileSubject.value;
    if (!profile) {
      profile = await this.getUserProfile();
    }

    const hasVerifiedMembership = Array.isArray(profile?.memberships) &&
      profile.memberships.some((m: any) => m.statementVerified);
    const isT1Elite = profile?.t1Elite === true;

    let connectionsCount = 0;
    let reviewsCount = 0;
    let accountAgeMonths = 0;
    let meetsCommunityCriteria = false;

    if (!hasVerifiedMembership && !isT1Elite) {
      const [fromSnap, toSnap, reviewsSnap] = await Promise.all([
        getDocs(query(collection(this.firebaseService.firestore, 'flight-connections'), where('fromId', '==', user.uid))),
        getDocs(query(collection(this.firebaseService.firestore, 'flight-connections'), where('toId', '==', user.uid))),
        getDocs(collection(this.firebaseService.firestore, `users/${user.uid}/reviews`))
      ]);

      connectionsCount = fromSnap.size + toSnap.size;
      reviewsCount = reviewsSnap.size;

      let createdAt: Date;
      if (profile?.createdAt?.toDate) {
        createdAt = profile.createdAt.toDate();
      } else if (profile?.createdAt instanceof Date) {
        createdAt = profile.createdAt;
      } else if (profile?.createdAt) {
        createdAt = new Date(profile.createdAt);
      } else {
        createdAt = new Date(0);
      }

      accountAgeMonths = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24 * 30);
      meetsCommunityCriteria = connectionsCount >= 5 && reviewsCount >= 3 && accountAgeMonths >= 2;
    }

    const eligible = hasVerifiedMembership || isT1Elite || meetsCommunityCriteria;

    return {
      eligible,
      hasVerifiedMembership,
      isT1Elite,
      meetsCommunityCriteria,
      connectionsCount,
      reviewsCount,
      accountAgeMonths,
    };
  }

  // Private method to listen for real-time profile updates
  private listenToUserProfile(uid: string) {
    const userDocRef = doc(this.firebaseService.firestore, 'users', uid);
    // Save unsubscribe handle so we can stop listening later
    this.profileUnsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const profile = snapshot.data();
          this.userProfileSubject.next(profile);
        } else {
          this.userProfileSubject.next(null);
        }
      },
      (error) => {
        console.error('Error listening to user profile:', error);
        this.userProfileSubject.next(null);
      }
    );
  }

  public stopListeningToProfile(): Promise<void> {
    if (this.profileUnsubscribe) {
      this.profileUnsubscribe();
      this.profileUnsubscribe = undefined;
    }
    this.userProfileSubject.next(null);

    return Promise.resolve();
  }

  getPrivateName(fullName?: string | null) {
    if (!fullName || !fullName.trim()) {
      return '';
    }

    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    return lastName ? `${firstInitial}. ${lastName}` : `${firstInitial}.`;
  }

  async isPhoneNumberUnique(number: string): Promise<boolean> {
    const docRef = doc(this.firebaseService.firestore, `verified-numbers`, number);
    const docSnap = await getDoc(docRef);

    return !docSnap.exists();
  }

  async saveUnsupportedProvider(provider: string, userEmail: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    };
    const docRef = doc(this.firebaseService.firestore, `user-provider-requests`, user.uid);
    await setDoc(docRef, {
      userId: user.uid,
      userEmail: userEmail,
      provider,
      createdAt: new Date()
    });
  }

  async deleteUnsupportedProvider(): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    }

    const docRef = doc(this.firebaseService.firestore, `user-provider-requests`, user.uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      await deleteDoc(docRef);
    }
  }

  async savePhoneNumberWithUserId(number: string, userId: string): Promise<void> {
    const docRef = doc(this.firebaseService.firestore, `verified-numbers`, number);
    await setDoc(docRef, {
      userId: userId
    });
  }

  sendVerificationCode(phoneNumber: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const url = this.phoneVerificationUrl();
    const body = { phoneNumber };
    return this.http.post(url, body, { headers });
  }

  verifyCode(phoneNumber: string, verificationCode: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const url = this.phoneVerificationUrl();
    const body = { phoneNumber, verificationCode };
    return this.http.post(url, body, { headers });
  }

  private phoneVerificationUrl(): string {
    return `https://${environment.apiBaseURL}.cloudfunctions.net/handlePhoneVerification`;
  }
}
